import { defaultStoreUtils } from '@databases/redux';
import {
  DatabaseFilter,
  DatabaseFilterType,
  isFilterWithUsageType,
} from '@databases/types/database/view';
import { getStringRepresentation } from '@databases/utils/database/field-types';
import { getDatabaseFieldOptional } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { useIntl } from 'react-intl-next';

import { messages } from '../i18n';

import { useFilterTypeLabel } from './useFilterTypeLabel';

type UseFilterStringRepresentationOptions = { contentId: string; filter: DatabaseFilter };

export const useFilterStringRepresentation = ({
  contentId,
  filter,
}: UseFilterStringRepresentationOptions) => {
  const { formatMessage } = useIntl();
  const fieldId = isFilterWithUsageType(filter) ? undefined : filter.fieldId;

  const [fieldName, fieldType, filterValueStringRepresentation] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdRequired(state, {
        contentId,
      });
      const field = getDatabaseFieldOptional(database, fieldId);

      const filterValueStringRepresentation =
        !field ||
        isFilterWithUsageType(filter) ||
        filter.type === DatabaseFilterType.EMPTY
          ? undefined
          : typeof filter.value === 'string'
          ? filter.value
          : getStringRepresentation({ database, field, value: filter.value }, state);

      return [field?.name, field?.type, filterValueStringRepresentation] as const;
    });

  const filterTypeLabel = useFilterTypeLabel({
    fieldType,
    filter,
  });

  if (!fieldId) {
    return formatMessage(messages['view-settings.filter-panel.fieldless-filter-name']);
  }

  return filterValueStringRepresentation
    ? `“${fieldName}” ${filterTypeLabel} ${filterValueStringRepresentation}`
    : `“${fieldName}” ${filterTypeLabel}`;
};
